import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { InfoOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { alpha, Box, Typography } from '@mui/material'
import { routes } from '@/router'
import { UserProfile } from '@/types'
import { sessionButtonSx } from '../session-button'
import { SessionScreen } from '../session-screen'
import { GraduationPicker } from './graduation-picker'
import {
  estimatedSemester,
  flowProgress,
  graduationDate,
  graduationYearMin,
  isGraduationMonthEnabled,
  nextProfileStep,
  previousProfileStep,
} from './profile-steps'
import { ProfileStepFrame } from './profile-step-frame'
import { saveProfile } from './save-profile'

function readGraduation(user: UserProfile) {
  const value = user.profile?.graduation_date
  if (!value) {
    return { year: graduationYearMin + 1, month: null as number | null }
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return { year: graduationYearMin + 1, month: null as number | null }
  }
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() }
}

export function ProfileGraduationPage() {
  return (
    <ProfileStepFrame step="graduation">
      {(user) => <GraduationStep user={user} />}
    </ProfileStepFrame>
  )
}

function GraduationStep({ user }: { user: UserProfile }) {
  const navigate = useNavigate()
  const initial = readGraduation(user)
  const [year, setYear] = useState(initial.year)
  const [month, setMonth] = useState<number | null>(initial.month)
  const [saving, setSaving] = useState(false)
  const previous = previousProfileStep('graduation', user)

  async function continueGraduation() {
    if (month == null || !isGraduationMonthEnabled(year, month) || saving) {
      return
    }
    setSaving(true)

    try {
      const refreshed = await saveProfile({
        graduation_date: graduationDate(year, month),
      })
      const step = nextProfileStep(refreshed)
      navigate(step ? routes.session.profile[step] : routes.home)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SessionScreen
      title="Quando você se forma?"
      subtitle="Usamos sua previsão de formatura para mostrar os produtos certos para você."
      progress={flowProgress('graduation', user)}
      onBack={() =>
        navigate(
          previous
            ? routes.session.profile[previous]
            : routes.session.profile.situation,
        )
      }
      footer={
        <LoadingButton
          size="large"
          fullWidth
          loading={saving}
          disabled={month == null || !isGraduationMonthEnabled(year, month)}
          onClick={continueGraduation}
          sx={sessionButtonSx()}
        >
          Continuar
        </LoadingButton>
      }
    >
      <GraduationPicker
        year={year}
        month={month}
        onChange={(nextYear, nextMonth) => {
          setYear(nextYear)
          setMonth(nextMonth)
        }}
      />

      {month != null && isGraduationMonthEnabled(year, month) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.18),
            borderRadius: 1.5,
            p: 2,
          }}
        >
          <InfoOutlined color="primary" />
          <Typography variant="body2">
            Você está no <b>{estimatedSemester(year, month)}º semestre</b>. Não
            se preocupe, essa é só uma estimativa.
          </Typography>
        </Box>
      )}
    </SessionScreen>
  )
}
